import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, ChevronLeft, ChevronRight, ExternalLink, Hammer } from "lucide-react";
import Reveal from "@/components/Reveal";
import { COLLECTION } from "@/data/site";

function Carousel({ images }) {
  const [idx, setIdx] = useState(0);
  const total = images.length;

  const go = (n) => setIdx((idx + n + total) % total);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowLeft") go(-1);
      if (e.key === "ArrowRight") go(1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [idx, total]);

  const current = images[idx];

  return (
    <div data-testid="car-carousel" className="w-full">
      <div className="relative tile aspect-[16/10] bg-[color:var(--ink)]">
        <img
          key={current.url}
          src={current.url}
          alt={current.caption || "Motor car"}
          className="w-full h-full object-contain bg-[color:var(--ink)]"
        />

        {total > 1 && (
          <>
            <button
              onClick={() => go(-1)}
              data-testid="carousel-prev-btn"
              aria-label="Previous photo"
              className="absolute left-4 top-1/2 -translate-y-1/2 bg-[color:var(--bg)]/85 hover:bg-[color:var(--bg)] border border-[color:var(--accent)] p-3 transition"
            >
              <ChevronLeft size={20} />
            </button>
            <button
              onClick={() => go(1)}
              data-testid="carousel-next-btn"
              aria-label="Next photo"
              className="absolute right-4 top-1/2 -translate-y-1/2 bg-[color:var(--bg)]/85 hover:bg-[color:var(--bg)] border border-[color:var(--accent)] p-3 transition"
            >
              <ChevronRight size={20} />
            </button>
            <div className="absolute bottom-4 right-4 bg-[color:var(--bg)]/85 border border-[color:var(--accent)] px-3 py-1 text-xs tracking-[0.22em] uppercase">
              {String(idx + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
            </div>
          </>
        )}
      </div>

      {current.caption && (
        <div className="mt-5 text-base text-[color:var(--ink-secondary)] italic font-display text-xl">
          {current.caption}
        </div>
      )}

      {total > 1 && (
        <div className="mt-8 grid grid-cols-4 md:grid-cols-6 lg:grid-cols-10 gap-3">
          {images.map((img, i) => (
            <button
              key={img.url}
              onClick={() => setIdx(i)}
              data-testid={`carousel-thumb-${i}`}
              className={`tile aspect-square overflow-hidden ${
                i === idx ? "ring-2 ring-[color:var(--brg)] ring-offset-2 ring-offset-[color:var(--surface)]" : ""
              }`}
              aria-label={`Show photo ${i + 1}`}
            >
              <img src={img.url} alt="" className="w-full h-full object-cover" loading="lazy" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function CarDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const car = COLLECTION.find((c) => c.slug === slug);

  if (!car) {
    return (
      <div className="parchment py-32 text-center">
        <h1 className="font-display text-4xl">Motor car not found</h1>
        <Link to="/collection" className="btn-ghost mt-8 inline-flex">Back to the Collection</Link>
      </div>
    );
  }

  const index = COLLECTION.findIndex((c) => c.slug === slug);
  const prev = COLLECTION[(index - 1 + COLLECTION.length) % COLLECTION.length];
  const next = COLLECTION[(index + 1) % COLLECTION.length];

  const images =
    car.images && car.images.length > 0
      ? car.images
      : [{ url: car.image, caption: `${car.year} ${car.make} ${car.model}` }];

  return (
    <div data-testid={`car-detail-${car.slug}`}>
      {/* Header */}
      <section className="parchment border-b border-[color:var(--accent)]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-20">
          <button
            onClick={() => navigate("/collection")}
            data-testid="car-back-link"
            className="nav-link inline-flex items-center gap-2 mb-10"
          >
            <ArrowLeft size={14} /> Back to the Collection
          </button>

          <Reveal>
            <div className="flex items-baseline justify-between flex-wrap gap-6">
              <div>
                <div className="eyebrow mb-4">{car.year} · Motor car №{String(index + 1).padStart(2, "0")}</div>
                <h1 className="font-display text-6xl md:text-8xl tracking-tighter leading-[0.92]">
                  {car.make} <span className="italic text-[color:var(--brg)]">{car.model}</span>
                </h1>
              </div>
              {car.status && (
                <div className="flex items-center gap-3 border border-[color:var(--oxblood)] text-[color:var(--oxblood)] px-4 py-2">
                  <Hammer size={16} />
                  <span className="text-xs uppercase tracking-[0.22em]">{car.status}</span>
                </div>
              )}
            </div>
          </Reveal>

          {car.note && (
            <Reveal delay={150}>
              <p className="mt-10 max-w-3xl text-lg md:text-xl text-[color:var(--ink-secondary)] leading-relaxed">
                {car.note}
              </p>
            </Reveal>
          )}
        </div>
      </section>

      {/* Carousel */}
      <section className="bg-[color:var(--surface)] border-b border-[color:var(--accent)]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24">
          <Reveal>
            <Carousel images={images} />
          </Reveal>
        </div>
      </section>

      {/* Story */}
      {car.story && (
        <section className="parchment border-b border-[color:var(--accent)]">
          <div className="max-w-4xl mx-auto px-6 md:px-12 py-20 md:py-28">
            <Reveal>
              <div className="eyebrow mb-6">The Story</div>
              <div className="font-display text-2xl md:text-3xl leading-[1.45] text-[color:var(--ink)] space-y-8">
                {car.story.split("\n\n").map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
              </div>
            </Reveal>
          </div>
        </section>
      )}

      {/* External album link */}
      {car.link && (
        <section className="parchment">
          <div className="max-w-7xl mx-auto px-6 md:px-12 py-14 text-center">
            <a
              href={car.link}
              target="_blank"
              rel="noreferrer"
              data-testid={`car-original-album-${car.slug}`}
              className="btn-ghost"
            >
              View the original britcars.us album <ExternalLink size={14} />
            </a>
          </div>
        </section>
      )}

      {/* Prev / Next */}
      <section className="border-t border-[color:var(--accent)] bg-[color:var(--surface)]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 grid md:grid-cols-2 gap-6">
          <Link
            to={`/collection/${prev.slug}`}
            data-testid="car-prev-link"
            className="group flex items-center gap-5 border border-[color:var(--accent)] p-6 hover:border-[color:var(--brg)] transition"
          >
            <ChevronLeft className="text-[color:var(--brg)]" size={24} />
            <div className="tile w-20 h-16 shrink-0">
              <img src={prev.image} alt="" className="w-full h-full object-cover" />
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.22em] text-[color:var(--ink-muted)]">Previous</div>
              <div className="font-display text-xl group-hover:text-[color:var(--brg)] transition">
                {prev.year} {prev.make} <span className="italic">{prev.model}</span>
              </div>
            </div>
          </Link>

          <Link
            to={`/collection/${next.slug}`}
            data-testid="car-next-link"
            className="group flex items-center gap-5 border border-[color:var(--accent)] p-6 hover:border-[color:var(--brg)] transition md:flex-row-reverse md:text-right"
          >
            <ChevronRight className="text-[color:var(--brg)]" size={24} />
            <div className="tile w-20 h-16 shrink-0">
              <img src={next.image} alt="" className="w-full h-full object-cover" />
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.22em] text-[color:var(--ink-muted)]">Next</div>
              <div className="font-display text-xl group-hover:text-[color:var(--brg)] transition">
                {next.year} {next.make} <span className="italic">{next.model}</span>
              </div>
            </div>
          </Link>
        </div>
      </section>
    </div>
  );
}
